<template>
  <div v-if="mobileVideoId || landscapeVideoId" class="c-startpage-avatar-youtube">
    <!-- MOBILE -->
    <div v-if="mobileVideoId" class="sm:hidden" :class="landscapeVideoAspectRatio == 'hoch' ? 'bg-black':''">
      <div class="youtube-player" :data-type="mobileVideoAspectRatio">
        <iframe
          title="Youtube Video"
          :data-type="mobileVideoAspectRatio"
          video-type="short"
          :src="getVideoUrl(mobileVideoId)"
          loading="lazy"
          referrerpolicy="strict-origin-when-cross-origin"
          allow="accelerometer; gyroscope; encrypted-media; picture-in-picture"
          allowfullscreen
        />
      </div>
    </div>
    <!-- DESKTOP -->
    <div v-if="landscapeVideoId" class="hidden sm:block" :class="landscapeVideoAspectRatio == 'hoch' ? 'bg-black':''">
      <div class="youtube-player" :data-type="landscapeVideoAspectRatio">
        <iframe
          title="Youtube Video"
          :data-type="landscapeVideoAspectRatio"
          video-type="video"
          :src="getVideoUrl(landscapeVideoId)"
          loading="lazy"
          referrerpolicy="strict-origin-when-cross-origin"
          allow="accelerometer; gyroscope; encrypted-media; picture-in-picture"
          allowfullscreen
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface YoutubeVideo {
  landscapeVideo?: { value?: unknown, type?: unknown };
  portraitVideo?: { value?: unknown, type?: unknown };
}

const props = defineProps<{ video?: YoutubeVideo }>();

const YOUTUBE_VIDEO_ID_PATTERN = /^[\w-]{11}$/;
const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);

const getVideoId = (value: unknown): string => {
  if (typeof value !== 'string') return '';

  const videoValue = value.trim();
  if (YOUTUBE_VIDEO_ID_PATTERN.test(videoValue)) return videoValue;

  try {
    const url = new URL(videoValue);
    const host = url.hostname.toLowerCase();
    const pathSegments = url.pathname.split('/').filter(Boolean);
    const videoId = host === 'youtu.be'
      ? pathSegments[0]
      : YOUTUBE_HOSTS.has(host)
        ? url.searchParams.get('v') ?? (['embed', 'shorts', 'live', 'v'].includes(pathSegments[0]) ? pathSegments[1] : '')
        : '';

    return videoId && YOUTUBE_VIDEO_ID_PATTERN.test(videoId) ? videoId : '';
  } catch {
    return '';
  }
};

const landscapeVideoId = computed(() => getVideoId(props.video?.landscapeVideo?.value));
const portraitVideoId = computed(() => getVideoId(props.video?.portraitVideo?.value));
const mobileVideoId = computed(() => portraitVideoId.value || landscapeVideoId.value);
const landscapeVideoAspectRatio = computed(() =>
  props.video?.landscapeVideo?.type === 'short' ? 'hoch' : 'quer',
);
const mobileVideoAspectRatio = computed(() =>
  portraitVideoId.value ? 'hoch' : landscapeVideoAspectRatio.value,
);

const getVideoUrl = (videoId: string): string => `https://www.youtube-nocookie.com/embed/${videoId}`;
</script>

<style lang="postcss">
.youtube-player {
  width: 100%;
  aspect-ratio: 16 / 9;
}

.youtube-player[data-type="quer"] {
  @apply w-full;
  aspect-ratio: 16 / 9;
  width:100%; 
  min-height:640px;
}

.youtube-player[data-type="hoch"] {
  @apply bg-black flex justify-center items-center sm:max-w-96 sm:mx-auto;
  aspect-ratio: 9 / 16;
  min-height:640px;
}

.youtube-player iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  border-radius: 12px;
}
</style>
